import { Provider } from 'react-redux';
import * as Sentry from '@sentry/react';
import TagManager from 'react-gtm-module';

import Application from 'components/Application';
import { ConfigProvider } from 'providers/useConfig/ConfigContext';
import { matchIsMobileDevice } from 'style/styled-components/cssMediaQueries';
import React, { FC, PropsWithChildren } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router';
import { ToastProvider } from './providers/useToast/ToastProvider';
import packageInfo from '../package.json';
import { AuthProvider, useAuth } from './providers/useAuth/AuthContext';
import { PlayerProvider } from './providers/player/PlayerContext';
import { setLocalStorage } from './utils/fnStorage';
import localConfig from './config/localConfig';
import { composeProviders } from './utils/composeProviders';

import './style/scss/global.scss';

import { EpgProvider } from './providers/useEpgStore/EpgContext';
import { EpgModalDetailsProvider } from './providers/useEpgStore/EpgModalContext';
import { store } from './providers/reducers';
import { AppProvider, useApp } from './providers/useApp/AppContext';
import { ThemeConfigConnector } from './components/ThemeConfigConnector/ThemeConfigConnnector';
import { PurchaseProvider } from './providers/usePurchase/PurchaseContext';
import { UpsellFlowProvider } from './providers/useUpsellFlow/UpsellFlowContext';
import { BookmarksProvider } from './providers/useBookmarks/BookmarksContext';
import { WatchlistProvider } from './providers/useWatchlist/WatchlistContext';
import { PlaybackProvider } from './providers/usePlayback/PlaybackContext';
import { SearchProvider } from './providers/useSearch/SearchContext';
import { ErrorBoundary } from './components/ErrorBoundary/ErrorBoundary';
import { PinProvider } from './providers/usePin/PinContext';
import { SubscribeProvider } from './providers/useSubscription/SubscriptionContext';
import { ChannelsProvider } from './providers/useChannels/useChannels';
import { PlayOptionsProvider } from './providers/usePlayOptions/PlayOptionsContext';
import { Notification } from './components/Notification/Notification';
import { SECONDS } from './utils/TimeUnit';
import { RecordingProvider } from './providers/useRecording/RecordingContext';
import { CastProvider } from './providers/cast/CastContext';
import { HintProvider } from './providers/useHint/HintContext';
import { NavigationProvider } from './providers/useNavigation/NavigationContext';
import { QueryProvider } from './providers/QueryProvider';

// eslint-disable-next-line no-var, no-underscore-dangle
declare var __VERSION__: string;
// eslint-disable-next-line no-var, no-underscore-dangle
declare var __COMMITHASH__: string;
// eslint-disable-next-line no-var
declare var chromeCastStatus: boolean;

declare global {
    export interface Window {
        __VERSION__: string;
        __COMMITHASH__: string;
        chromeCastStatus: boolean;
        Cypress: any;
        shaka: any;
    }
}

// eslint-disable-next-line no-underscore-dangle
window.__VERSION__ = __VERSION__;
// eslint-disable-next-line no-underscore-dangle
window.__COMMITHASH__ = __COMMITHASH__;

window.chromeCastStatus = chromeCastStatus;

// Set document title
document.title = localConfig.title;

setLocalStorage('isMobile', matchIsMobileDevice());

const APP_BUNDLE_PATH = '/static/js/';

const isOutsideAppBundle = (event: Sentry.ErrorEvent): boolean => {
    const frames = (event.exception?.values ?? []).flatMap((value) => value.stacktrace?.frames ?? []);

    if (!frames.length) {
        return false;
    }

    return !frames.some((frame) => frame.filename?.includes(APP_BUNDLE_PATH));
};

Sentry.init({
    dsn: 'https://e09f30092671ff66c000f62170957efa@o850291.ingest.us.sentry.io/4511733555527680',
    enabled: process.env.NODE_ENV === 'production',
    environment: process.env.NODE_ENV,
    release: `WEB@${packageInfo.version}`,
    dist: packageInfo.version.replace(/\./g, ''),
    integrations: [],
    tracesSampleRate: 0,
    attachStacktrace: true,
    ignoreErrors: [
        'Non-Error exception captured',
        'The user aborted a request',
        'Network Error',
        'signal is aborted',
        'The operation was aborted',
        'AbortError',
        'Request aborted',
        'Pending operation cancelled by exitFullscreen() call',
        'Cannot request fullscreen without transient activation',
        'transient activation',
        'window.webkit.messageHandlers',
    ],
    beforeSend: (event) => (isOutsideAppBundle(event) ? null : event),
});

const tagManagerArgs = {
    gtmId: 'GTM-KZFW4Z',
};

TagManager.initialize(tagManagerArgs);

const SentryUserTracker: FC<PropsWithChildren> = ({ children }) => {
    const { userInfoDetails } = useAuth();

    if (userInfoDetails) {
        const { sub, subscription, deviceType } = userInfoDetails;
        Sentry.setUser({
            id: subscription,
            deviceType,
            uuid: sub,
        });
    }

    return <>{children}</>;
};

const ConfiguredToastProvider: FC<PropsWithChildren> = ({ children }) => {
    const { isDesktop } = useApp();

    return (
        <ToastProvider
            components={{ Toast: Notification as unknown as React.ComponentType<import('./providers/useToast/ToastProvider').ToastProps> }}
            placement={isDesktop() ? 'top-right' : 'bottom-right'}
            autoDismissTimeout={SECONDS.toMillis(5)}
        >
            {children}
        </ToastProvider>
    );
};

const DataProviders = composeProviders([
    // State management
    ({ children }) => <Provider store={store}>{children}</Provider>,

    // Sentry
    SentryUserTracker,

    // Navigation provider (must be before SearchProvider)
    NavigationProvider,

    // Content providers
    SearchProvider,
    BookmarksProvider,
    WatchlistProvider,
    PlaybackProvider,

    // UI providers
    ConfiguredToastProvider,
    PinProvider,
    HintProvider,

    // Media providers
    CastProvider,
    PlayerProvider,
    ChannelsProvider,

    // EPG providers
    EpgModalDetailsProvider,
    EpgProvider,

    // Transaction providers
    SubscribeProvider,
    PurchaseProvider,
    UpsellFlowProvider,

    // Recording provider
    RecordingProvider,

    // Play options provider
    PlayOptionsProvider,
]);

const CombinedProvider = composeProviders([
    AppProvider,
    ConfigProvider,
    QueryProvider,
    ({ children }) => <ErrorBoundary innerLevel={false}>{children}</ErrorBoundary>,
    AuthProvider,
    ({ children }) => <BrowserRouter basename={BASENAME}>{children}</BrowserRouter>,
    ThemeConfigConnector,
    ({ children }) => <ErrorBoundary innerLevel={true}>{children}</ErrorBoundary>,
    DataProviders,
]);

createRoot(document.getElementById('root')).render(
    <CombinedProvider>
        <Application />
    </CombinedProvider>
);
